"use client"

import { PricedProduct, PricedVariant } from "@medusajs/medusa/dist/types/pricing"
import { useEffect, useState } from "react"

const COOKIE_NAME = "last_checkout_product"

// Custom event for cookie changes
const COOKIE_CHANGE_EVENT = "last_checkout_product_change"

export interface LastCheckoutProduct {
  id: string
  title: string
  handle: string
  price: string
  variantId: string
  variantLabel?: string
  pagosPrice?: string
  thumbnail?: string
  timestamp: string
}

/**
 * Saves the last product that a user attempted to checkout with
 *
 * @param options - Product, variant, and pricing data for saving
 */
export const saveLastCheckoutProduct = (options: {
  product: PricedProduct
  variant: PricedVariant
  price: string
  expiryHours?: number
  hasMultipleVariants?: boolean
}): void => {
  try {
    const { product, variant, price, expiryHours, hasMultipleVariants } = options

    // Validate required fields
    if (!product?.id) throw new Error("Product ID is required")
    if (!product?.title) throw new Error("Product title is required")
    if (!product?.handle) throw new Error("Product handle is required")
    if (!product?.thumbnail) throw new Error("Product thumbnail is required")
    if (!price) throw new Error("Price is required")
    if (!variant?.id) throw new Error("Variant ID is required")

    const productData: LastCheckoutProduct = {
      id: product.id,
      title: product.title,
      handle: product.handle,
      thumbnail: product.thumbnail,
      variantId: variant.id,
      variantLabel: hasMultipleVariants && variant.title ? variant.title : undefined,
      price,
      timestamp: new Date().toISOString(),
    }

    // Convert hours to seconds for cookie max-age (default 6 hours)
    const validExpiryHours = Math.min(Math.max(1, expiryHours ?? 6), 24) // Limit to a maximum of 24 hours and minimum of 1 hour
    const maxAge = validExpiryHours * 60 * 60

    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(productData))}; path=/; max-age=${maxAge}; SameSite=Strict; Secure`

    // Dispatch event to notify listeners that the cookie has changed
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(COOKIE_CHANGE_EVENT, { detail: productData }))
    }
  } catch (e) {
    console.error("Error saving last checkout product to cookie:", e)
  }
}

/**
 * Validates that the parsed data has the expected structure for a LastCheckoutProduct
 *
 * @param data - The parsed data to validate
 * @returns True if the data is valid, false otherwise
 */
export const validateLastCheckoutProduct = (data: unknown): data is LastCheckoutProduct => {
  if (typeof data !== "object" || data === null) {
    return false
  }

  const product = data as Record<string, unknown>

  // Validate required string fields
  const requiredStringFields: (keyof LastCheckoutProduct)[] = ["id", "handle", "price", "variantId", "title", "timestamp"]
  for (const field of requiredStringFields) {
    if (typeof product[field] !== "string") {
      console.error(`Invalid or missing field: ${field}`)
      return false
    }
  }

  // Validate optional string fields
  const optionalStringFields: (keyof LastCheckoutProduct)[] = ["variantLabel", "pagosPrice", "thumbnail"]
  for (const field of optionalStringFields) {
    if (product[field] !== undefined && typeof product[field] !== "string") {
      console.error(`Invalid optional field: ${field}`)
      return false
    }
  }

  // Validate timestamp format
  try {
    return true
  } catch (e) {
    console.error("Invalid timestamp format in last checkout product", e)
    return false
  }
}

/**
 * Retrieves the last product that a user attempted to checkout with
 *
 * @returns The last checkout product or null if not found or invalid
 */
export const getLastCheckoutProduct = (): LastCheckoutProduct | null => {
  try {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${COOKIE_NAME}=`)

    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(";").shift()
      if (cookieValue) {
        const parsedData = JSON.parse(decodeURIComponent(cookieValue))

        if (validateLastCheckoutProduct(parsedData)) {
          return parsedData
        }

        console.error("Invalid data structure in last checkout product cookie")
      }
    }
  } catch (e) {
    console.error("Error retrieving last checkout product from cookie:", e)
  }
  return null
}

/**
 * Removes the last checkout product cookie
 */
export const clearLastCheckoutProduct = (): void => {
  try {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`

    // Dispatch event to notify listeners that the cookie has been cleared
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(COOKIE_CHANGE_EVENT, { detail: null }))
    }
  } catch (e) {
    console.error("Error clearing last checkout product cookie:", e)
  }
}

/**
 * Hook to watch for changes to the last checkout product cookie
 *
 * @returns The last checkout product or null if not found or invalid
 */
export const useLastCheckoutProduct = (): LastCheckoutProduct | null => {
  const [lastProduct, setLastProduct] = useState<LastCheckoutProduct | null>(() => {
    // Initialize with current value if we're in a browser environment
    if (typeof window !== "undefined") {
      return getLastCheckoutProduct()
    }
    return null
  })

  useEffect(() => {
    // Handler for cookie change events
    const handleCookieChange = (event: CustomEvent<LastCheckoutProduct | null>) => {
      if (event && event.detail) {
        // Only validate if there is data in event.detail
        validateLastCheckoutProduct(event.detail) ? setLastProduct(event.detail) : setLastProduct(null)
      } else {
        // If event.detail is null (cookie was cleared), set lastProduct to null
        setLastProduct(null)
      }
    }

    // Add event listener for cookie changes
    if (typeof window !== "undefined") {
      window.addEventListener(COOKIE_CHANGE_EVENT, handleCookieChange as EventListener)
    }

    // Cleanup function to remove event listener
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener(COOKIE_CHANGE_EVENT, handleCookieChange as EventListener)
      }
    }
  }, [])

  return lastProduct
}
